import React, {useMemo, useRef, useState} from 'react';
import {useAuthGrant} from 'services/auth/auth.hooks';
import {useComponentProps} from 'helpers/hooks/utils';
import Box from 'components/atoms/Layout/Box/Box';
import ActionLink from 'components/molecules/Links/ActionLink/ActionLink';
import {H6} from 'components/atoms/Text/Typography/Typography';
import StyledGrantForm from 'components/organisms/Forms/GrantForm/GrantForm.styles';
import {useAuthUser} from 'components/organisms/Providers/AuthProvider/AuthProvider';
import Form from 'components/organisms/Forms/Form/Form';
import utils from 'helpers/utils';
import {useLocation, useParams, useSearchParams} from 'react-router-dom';
import PropTypes from 'prop-types';
import constants from 'helpers/constants';

const GrantForm = (props) => {
  const {
    onSuccess,
    ...innerProps
  } = useComponentProps(props, 'GrantForm');

  const user = useAuthUser();

  const [searchParams] = useSearchParams();
  const location = useLocation();

  const { uuid } = useParams();
  const code = searchParams.get('code');

  const doGrant = useAuthGrant();
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const formRef = useRef(null);

  const pages = useMemo(() => {
    return {
      0: {
        title: `You are about to open a ${constants.basics.shortName} extension as ${utils.personName(user.firstName, user.lastName)}`,
        fields: {
          uuid: {
            label: 'Uuid',
            type: 'text',
            initial: uuid,
            required: false,
            FormFieldProps: {
              hidden: true
            }
          },
          code: {
            label: 'Code',
            type: 'text',
            initial: code,
            required: false,
            FormFieldProps: {
              hidden: true
            }
          }
        }
      }
    }
  }, [user, uuid, code])

  const handleSubmit = (values, actions) => {
    doGrant.mutation.mutateAsync(values)
      .then((res) => {
        setSuccess(true);
        onSuccess?.();

        if (res.response?.data?.data?.redirectUri) {
          window.location.href = res.response?.data?.data?.redirectUri;
        }
      })
      .catch(() => {
        setError(`The ${constants.basics.shortName} extension request could not be completed`);
      })
      .finally(() => {
        actions.setSubmitting(false);
      });
  };

  return <StyledGrantForm {...innerProps}>
    {error?.length > 0 ? <H6>{error}</H6> : (
      !success ? <Form ref={formRef}
                       pages={pages}
                       onSubmit={handleSubmit}
                       SubmitButtonProps={{children: 'Continue'}}/> :
        <H6>The authorization has been sent to the extension</H6>
    )}
    <Box className='auth-links'>
      {(success || error?.length > 0) ?
        <ActionLink action={{
          label: 'Log in instead?',
          navigation: {
            to: `/auth/login`,
            state: {
              from: null
            },
            replace: true
          }
        }}/> : (
          success ? null :
            <ActionLink action={{
              label: 'Switch account instead?',
              navigation: {
                to: `/auth/logout`,
                state: {
                  from: location,
                  resetSearchParams: true
                },
                replace: true
              }
            }}/>
        )}
    </Box>
  </StyledGrantForm>
};

GrantForm.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  onSuccess: PropTypes.func
};

export default GrantForm;
